import { colors } from '@/styles';
import { Signal } from '@/models/data/Signal';
import { pixelMask, pixelRect } from '@/utils/tilePixels';
import {
  TileSideIO,
  TileMeta,
  type ConnectionFlags,
  TileGraphicsContext,
} from '@/models/tiles/TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import * as PIXI from 'pixi.js';

const getSideIOType = (ioSide: TileSideIOType | TileSideIO): TileSideIOType => {
  return typeof ioSide === 'string' ? ioSide : ioSide.type;
};

export const defaultTileGraphics = (graphics: PIXI.Graphics) => {
  graphics.rect(0, 0, 120, 120);
  graphics.fill(colors.tileBackground);
};

export const sideGraphics = (
  graphics: PIXI.Graphics,
  ioSides: Record<Side, TileSideIOType | TileSideIO>,
  highColor: PIXI.ColorSource,
  lowColor: PIXI.ColorSource,
  meta: TileMeta,
) => {
  const drawArrow = (
    graphics: PIXI.Graphics,
    angleDeg: number,
    direction: 'in' | 'out' | 'both',
    color: PIXI.ColorSource,
  ) => {
    const turn = (((angleDeg / 90) % 4) + 4) % 4;
    if (direction === 'out' || direction === 'both')
      pixelMask(
        graphics,
        ['##...', '.##..', '..##.', '.##..', '##...'],
        19,
        10,
        color,
        turn,
      );
    if (direction === 'in' || direction === 'both')
      pixelMask(
        graphics,
        ['...##', '..##.', '.##..', '..##.', '...##'],
        19,
        10,
        color,
        turn,
      );
  };

  // Top
  if (getSideIOType(ioSides[Side.TOP]) === TileSideIOType.INPUT) {
    const color = meta.inputs.top === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 270, 'in', color);
  } else if (getSideIOType(ioSides[Side.TOP]) === TileSideIOType.OUTPUT) {
    const color = meta.outputs.top === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 270, 'out', color);
  } else if (getSideIOType(ioSides[Side.TOP]) === TileSideIOType.INPUT_OUTPUT) {
    const color = [meta.inputs.top, meta.outputs.top].includes(Signal.HIGH)
      ? highColor
      : lowColor;

    drawArrow(graphics, 270, 'both', color);
  }

  // Right
  if (getSideIOType(ioSides[Side.RIGHT]) === TileSideIOType.INPUT) {
    const color = meta.inputs.right === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 0, 'in', color);
  } else if (getSideIOType(ioSides[Side.RIGHT]) === TileSideIOType.OUTPUT) {
    const color = meta.outputs.right === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 0, 'out', color);
  } else if (getSideIOType(ioSides[Side.RIGHT]) === TileSideIOType.INPUT_OUTPUT) {
    const color = [meta.inputs.right, meta.outputs.right].includes(Signal.HIGH)
      ? highColor
      : lowColor;

    drawArrow(graphics, 0, 'both', color);
  }

  // Bottom
  if (getSideIOType(ioSides[Side.BOTTOM]) === TileSideIOType.INPUT) {
    const color = meta.inputs.bottom === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 90, 'in', color);
  } else if (getSideIOType(ioSides[Side.BOTTOM]) === TileSideIOType.OUTPUT) {
    const color = meta.outputs.bottom === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 90, 'out', color);
  } else if (getSideIOType(ioSides[Side.BOTTOM]) === TileSideIOType.INPUT_OUTPUT) {
    const color = [meta.inputs.bottom, meta.outputs.bottom].includes(Signal.HIGH)
      ? highColor
      : lowColor;

    drawArrow(graphics, 90, 'both', color);
  }

  // Left
  if (getSideIOType(ioSides[Side.LEFT]) === TileSideIOType.INPUT) {
    const color = meta.inputs.left === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 180, 'in', color);
  } else if (getSideIOType(ioSides[Side.LEFT]) === TileSideIOType.OUTPUT) {
    const color = meta.outputs.left === Signal.HIGH ? highColor : lowColor;

    drawArrow(graphics, 180, 'out', color);
  } else if (getSideIOType(ioSides[Side.LEFT]) === TileSideIOType.INPUT_OUTPUT) {
    const color = [meta.inputs.left, meta.outputs.left].includes(Signal.HIGH)
      ? highColor
      : lowColor;

    drawArrow(graphics, 180, 'both', color);
  }
};

export const connectionGraphics = (
  graphics: PIXI.Graphics,
  highColor: PIXI.ColorSource,
  lowColor: PIXI.ColorSource,
  meta: TileMeta,
  connections: ConnectionFlags,
  ioSides?: Record<Side, TileSideIOType | TileSideIO>,
  context?: TileGraphicsContext,
) => {
  // Skip rendering connection graphics during tile image generation
  if (context?.isTileImageGeneration) {
    return;
  }

  const getConnectionColor = (side: Side): PIXI.ColorSource => {
    if (ioSides) {
      const sideIO =
        typeof ioSides[side] === 'string'
          ? { type: ioSides[side] as TileSideIOType, forceConnection: false }
          : (ioSides[side] as TileSideIO);
      const isConnected = connections[side];
      const isForced = sideIO.forceConnection;

      if (isForced && !isConnected) {
        return '#00ff00';
      } else if (!isForced && !isConnected && sideIO.type !== TileSideIOType.NONE) {
        return '#808080';
      }
    }

    // Default signal-based colors
    if (connections[side]) {
      const signalHigh = (() => {
        switch (side) {
          case Side.TOP:
            return [meta.inputs.top, meta.outputs.top].includes(Signal.HIGH);
          case Side.RIGHT:
            return [meta.inputs.right, meta.outputs.right].includes(Signal.HIGH);
          case Side.BOTTOM:
            return [meta.inputs.bottom, meta.outputs.bottom].includes(Signal.HIGH);
          case Side.LEFT:
            return [meta.inputs.left, meta.outputs.left].includes(Signal.HIGH);
          default:
            return false;
        }
      })();
      return signalHigh ? highColor : lowColor;
    }

    return lowColor;
  };

  const drawConnection = (color: PIXI.ColorSource, angleDeg: number) => {
    // Nine pixels wide, centered on logical pixel 12, matching all wire arms.
    if (angleDeg === 0) pixelRect(graphics, 24, 8, 1, 9, color);
    else if (angleDeg === 90) pixelRect(graphics, 8, 24, 9, 1, color);
    else if (angleDeg === 180) pixelRect(graphics, 0, 8, 1, 9, color);
    else pixelRect(graphics, 8, 0, 9, 1, color);
  };

  // Top
  if (
    connections.top ||
    (ioSides &&
      (typeof ioSides[Side.TOP] === 'string'
        ? ioSides[Side.TOP] !== TileSideIOType.NONE
        : (ioSides[Side.TOP] as TileSideIO).type !== TileSideIOType.NONE))
  ) {
    const color = getConnectionColor(Side.TOP);
    drawConnection(color, 270);
  }

  // Right
  if (
    connections.right ||
    (ioSides &&
      (typeof ioSides[Side.RIGHT] === 'string'
        ? ioSides[Side.RIGHT] !== TileSideIOType.NONE
        : (ioSides[Side.RIGHT] as TileSideIO).type !== TileSideIOType.NONE))
  ) {
    const color = getConnectionColor(Side.RIGHT);
    drawConnection(color, 0);
  }

  // Bottom
  if (
    connections.bottom ||
    (ioSides &&
      (typeof ioSides[Side.BOTTOM] === 'string'
        ? ioSides[Side.BOTTOM] !== TileSideIOType.NONE
        : (ioSides[Side.BOTTOM] as TileSideIO).type !== TileSideIOType.NONE))
  ) {
    const color = getConnectionColor(Side.BOTTOM);
    drawConnection(color, 90);
  }

  // Left
  if (
    connections.left ||
    (ioSides &&
      (typeof ioSides[Side.LEFT] === 'string'
        ? ioSides[Side.LEFT] !== TileSideIOType.NONE
        : (ioSides[Side.LEFT] as TileSideIO).type !== TileSideIOType.NONE))
  ) {
    const color = getConnectionColor(Side.LEFT);
    drawConnection(color, 180);
  }
};
